import type { MailboxInfo } from "owa-client-types";
import { logGreyError } from "owa-analytics";
import { trace } from "owa-trace";

/**
 * Names of BizChat status events forwarded from the 1JS bridge.
 */
export type BizchatStatusEventType = "onUserSubmittedQuery" | "firstTokenReceived" | "QuerySubmit" | "queryResponseReceived" | "onConversationIdChanged" | "AbortByUser" | "EscapeHatchAbortByUser" | "AbortOnErrorInLocalPlugin" | "ClientQueryError" | "SydneyClientUnavailableError" | "ReceivedResponseFromSydneyError" | "BlankScreenErrorQuerySubmit";

/**
 * Read-only context handed to every subscriber.
 */
export interface BizchatStatusEventContext {
    readonly eventType: BizchatStatusEventType;
    readonly payload: unknown;
    readonly mailboxInfo: MailboxInfo;
    readonly targetWindow: Window | undefined;
}

/**
 * Primitive misc-data a subscriber may merge into the milestone telemetry row.
 * Surface-agnostic on purpose — this package knows nothing about the consuming
 * feature's keys (calendar IDA, mail vibe, …); each feature declares its own
 * typed shape and supplies its own correlation ids.
 *
 * Constrained to primitives — never widened to `Record<string, unknown>` — so a
 * subscriber can't leak an object/array into the milestone misc data. The
 * dispatcher also enforces this at runtime as defense-in-depth.
 */
export type BizchatStatusSubscriberExtras = {
    readonly [key: string]: string | number | boolean | undefined;
};

/**
 * Subscriber callback. Returns properties to merge into the milestone
 * telemetry log, or `undefined` for pure side effects.
 */
export type BizchatStatusSubscriber = (context: BizchatStatusEventContext) => BizchatStatusSubscriberExtras | undefined;

const subscribers = new Set<BizchatStatusSubscriber>();

/**
 * Register a subscriber for BizChat status events. Returns a dispose
 * function that removes the subscriber.
 */
export function registerBizchatStatusSubscriber(subscriber: BizchatStatusSubscriber): () => void {
    subscribers.add(subscriber);
    return () => {
        subscribers.delete(subscriber);
    };
}

/**
 * Invoke every registered subscriber and merge the property dictionaries
 * they return. Subscriber exceptions are isolated so milestone logging is
 * never broken by a buggy feature plugin.
 */
export function dispatchBizchatStatusSubscribers(context: BizchatStatusEventContext): Record<string, string | number | boolean> {
    if (subscribers.size === 0) {
        return {};
    }
    const merged: Record<string, string | number | boolean> = {};
    for (const subscriber of subscribers) {
        try {
            const result = subscriber(context);
            if (result) {
                // Runtime primitive filter so a buggy subscriber can never
                // leak an object/array into the milestone log misc data.
                for (const [key, value] of Object.entries(result)) {
                    const t = typeof value;
                    if (t === "string" || t === "number" || t === "boolean") {
                        merged[key] = value as string | number | boolean;
                    }
                }
            }
        }
        catch (error) {
            // Scrub the raw exception — PIE rule UnscrubbedExceptionObjectLogging.
            const err = error as Error | undefined;
            const scrubbed = new Error("BizchatStatusSubscriberError");
            logGreyError("BizchatStatusSubscriber_Error", context.mailboxInfo, scrubbed, {
                bizchatEventType: context.eventType,
                originalErrorName: err?.name,
            });
            if (process.env.NODE_ENV === "dev") {
                trace.warn(`[bizchatStatusSubscribers] subscriber threw for event ${context.eventType}: ${err?.name ?? "UnknownError"}`, "analytics");
            }
        }
    }
    return merged;
}

/**
 * Test-only helper: clears all registered subscribers.
 */
export function _clearBizchatStatusSubscribersForTest(): void {
    subscribers.clear();
}
